import React, { useState, useMemo } from 'react';
import './SkillBarChart.css';

const SkillBarChart = ({ skillScores, isLoading, error }) => {
  const [viewMode, setViewMode] = useState('domains'); // 'skills' or 'domains'

  // Calculate domain scores by averaging skill scores within each domain
  const domainScores = useMemo(() => {
    if (!skillScores || skillScores.length === 0) return [];
    
    // Domain mapping - this should match the structure from Dashboard
    const domainMapping = {
      // Reading and Writing domains
      "words_in_context": "Craft and Structure",
      "text_structure_and_purpose": "Craft and Structure", 
      "cross_text_connections": "Craft and Structure",
      "central_ideas_and_details": "Information and Ideas",
      "command_of_evidence": "Information and Ideas",
      "inferences": "Information and Ideas",
      "boundaries": "Standard English Conventions",
      "form_structure_and_sense": "Standard English Conventions",
      "rhetorical_synthesis": "Expression of Ideas",
      "transitions": "Expression of Ideas",
      
      // Math domains
      "linear_equations_in_one_variable": "Algebra",
      "linear_equations_in_two_variables": "Algebra",
      "linear_functions": "Algebra",
      "systems_of_two_linear_equations_in_two_variables": "Algebra",
      "linear_inequalities_in_one_or_two_variables": "Algebra",
      
      "equivalent_expressions": "Advanced Math",
      "nonlinear_equations_in_one_variable_and_systems_of_equations_in_two_variables": "Advanced Math",
      "nonlinear_functions": "Advanced Math",
      
      "ratios_rates_proportional_relationships_and_units": "Problem-Solving and Data Analysis",
      "percentages": "Problem-Solving and Data Analysis",
      "one_variable_data_distributions_and_measures_of_center_and_spread": "Problem-Solving and Data Analysis",
      "two_variable_data_models_and_scatterplots": "Problem-Solving and Data Analysis",
      "probability_and_conditional_probability": "Problem-Solving and Data Analysis",
      "inference_from_sample_statistics_and_margin_of_error": "Problem-Solving and Data Analysis",
      "evaluating_statistical_claims_observational_studies_and_experiments": "Problem-Solving and Data Analysis",
      
      "area_and_volume": "Geometry and Trigonometry",
      "lines_angles_and_triangles": "Geometry and Trigonometry",
      "right_triangles_and_trigonometry": "Geometry and Trigonometry",
      "circles": "Geometry and Trigonometry"
    };
    
    const domainGroups = {};
    
    // Group skills by domain
    skillScores.forEach(skill => {
      const domain = domainMapping[skill.skill];
      if (domain) {
        if (!domainGroups[domain]) {
          domainGroups[domain] = [];
        }
        domainGroups[domain].push(skill.score);
      }
    });
    
    // Calculate average score for each domain
    const domains = Object.entries(domainGroups).map(([domain, scores]) => ({
      skill: domain,
      skill_display_name: domain,
      score: Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
    }));
    
    // Sort domains by score (lowest to highest)
    return domains.sort((a, b) => a.score - b.score);
  }, [skillScores]);

  const currentData = viewMode === 'skills' ? skillScores : domainScores;
  if (isLoading) {
    return (
      <div className="skill-bar-chart">
        <div className="loading-message">Loading performance data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="skill-bar-chart">
        <div className="error-message">Error loading performance data: {error}</div>
      </div>
    );
  }

  if (!skillScores || skillScores.length === 0) {
    return (
      <div className="skill-bar-chart">
        <div className="no-data-message">No performance data available yet. Complete some practice sessions to see your skill breakdown!</div>
      </div>
    );
  }

  // Sort current data from lowest to highest score
  const sortedData = [...currentData].sort((a, b) => a.score - b.score);

  // Calculate the maximum score for scaling (should be 800, but let's be safe)
  const maxScore = Math.max(...sortedData.map(item => item.score), 800);
  const minScore = 200;
  const scoreRange = maxScore - minScore;

  // Function to get color based on score
  const getBarColor = (score) => {
    if (score >= 700) return '#4CAF50'; // Green for high scores
    if (score >= 600) return '#8BC34A'; // Light green
    if (score >= 500) return '#FFC107'; // Yellow for average
    if (score >= 400) return '#FF9800'; // Orange
    return '#F44336'; // Red for low scores
  };

  return (
    <div className="skill-bar-chart">
      <div className="chart-header">
        <h4>Your {viewMode === 'skills' ? 'Skill' : 'Domain'} Performance (Sorted by Score: Lowest to Highest)</h4>
        <div className="view-toggle">
          <button 
            className={`toggle-btn ${viewMode === 'skills' ? 'active' : ''}`}
            onClick={() => setViewMode('skills')}
          >
            Skills View
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'domains' ? 'active' : ''}`}
            onClick={() => setViewMode('domains')}
          >
            Domains View
          </button>
        </div>
      </div>
      <p className="chart-description">
        {viewMode === 'skills' 
          ? 'Skills on the left need the most improvement' 
          : 'Domains on the left need the most improvement (averaged from individual skills)'}
      </p>
      <div className="chart-container">
        <div className="y-axis">
          <div className="y-axis-label">800</div>
          <div className="y-axis-label">700</div>
          <div className="y-axis-label">600</div>
          <div className="y-axis-label">500</div>
          <div className="y-axis-label">400</div>
          <div className="y-axis-label">300</div>
          <div className="y-axis-label">200</div>
        </div>
        <div className="bars-container">
          {sortedData.map((item, index) => {
            const barHeight = ((item.score - minScore) / scoreRange) * 100;
            return (
              <div key={item.skill} className="bar-item">
                <div className="bar-wrapper">
                  <div 
                    className="bar"
                    style={{
                      height: `${barHeight}%`,
                      backgroundColor: getBarColor(item.score)
                    }}
                  >
                    <div className="score-label">{item.score}</div>
                  </div>
                </div>
                <div className="skill-label" title={item.skill_display_name}>
                  {item.skill_display_name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-color" style={{backgroundColor: '#F44336'}}></div>
          <span>Needs Improvement (200-399)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{backgroundColor: '#FF9800'}}></div>
          <span>Below Average (400-499)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{backgroundColor: '#FFC107'}}></div>
          <span>Average (500-599)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{backgroundColor: '#8BC34A'}}></div>
          <span>Good (600-699)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{backgroundColor: '#4CAF50'}}></div>
          <span>Excellent (700+)</span>
        </div>
      </div>
    </div>
  );
};

export default SkillBarChart;
